import { z } from 'zod'

/**
 * Source of truth for Document/Template input validation.
 * See `task.schema.ts` for the rationale (single zod = client UX + backend spec).
 */

export const DOCUMENT_TYPE = [
	'LEASE_CONTRACT', 'ACT_TRANSFER', 'ACT_RETURN',
	'APPENDIX', 'REGISTRATION_APPLICATION', 'OTHER',
] as const

export const DOCUMENT_STATUS = [
	'DRAFT', 'DATA_FILLED', 'SIGNED_BY_ME', 'SENT',
	'SIGNED_BY_TENANT', 'SIGNED_BY_BOTH',
] as const

export const DOCUMENT_ENTITY_TYPE = ['PROPERTY', 'RENTAL', 'DEAL', 'CONTACT'] as const

export const TEMPLATE_BLOCK_TYPE = ['SECTION', 'SUBSECTION', 'TEXT', 'FIELD'] as const
export const TEMPLATE_FIELD_TYPE = ['TEXT_AREA', 'DATE_PICKER', 'DATE_RANGE_PICKER'] as const

export const templateBlockSchema = z.object({
	id:         z.string().min(1),
	type:       z.enum(TEMPLATE_BLOCK_TYPE),
	content:    z.string().max(10_000),
	fieldType:  z.enum(TEMPLATE_FIELD_TYPE).optional(),
	blockOrder: z.number().int().min(0),
})

export const documentTemplateCreateSchema = z.object({
	title:           z.string().trim().min(1).max(200),
	type:            z.enum(DOCUMENT_TYPE),
	baseTemplateId:  z.string().min(1).optional(),
	blocks:          z.array(templateBlockSchema).min(1),
})

export const documentTemplateUpdateSchema = z.object({
	title:        z.string().trim().min(1).max(200).optional(),
	type:         z.enum(DOCUMENT_TYPE).optional(),
	isArchived:   z.boolean().optional(),
	blocks:       z.array(templateBlockSchema).min(1),
})

export const documentCreateSchema = z.object({
	templateId:     z.string().min(1).optional(),
	type:           z.enum(DOCUMENT_TYPE),
	title:          z.string().trim().min(1).max(200),
	entityType:     z.enum(DOCUMENT_ENTITY_TYPE),
	entityId:       z.string().min(1),
	filledDataJson: z.string().optional(),
})

export const documentUpdateSchema = z.object({
	title:          z.string().trim().min(1).max(200).optional(),
	filledDataJson: z.string().optional(),
	fileUrl:        z.string().url().optional(),
})

export const documentUpdateStatusSchema = z.object({
	status: z.enum(DOCUMENT_STATUS),
})

export type DocumentTemplateCreateInput = z.infer<typeof documentTemplateCreateSchema>
export type DocumentTemplateUpdateInput = z.infer<typeof documentTemplateUpdateSchema>
export type DocumentCreateInput          = z.infer<typeof documentCreateSchema>
export type DocumentUpdateInput          = z.infer<typeof documentUpdateSchema>
export type DocumentUpdateStatusInput    = z.infer<typeof documentUpdateStatusSchema>
